import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  where,
  limit,
  addDoc,
  getDoc,
  setDoc,
  serverTimestamp,
  increment,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from './firebaseConfig';

/**
 * Quotations Service - Handle all Firestore operations for quotations
 */

// Collection references
const QUOTATIONS_COLLECTION = 'quotations';
const QUOTATION_COUNTERS_COLLECTION = 'quotationCounters';

/**
 * Helper function to safely convert date to Firestore Timestamp
 * @param {Date} date - Date to convert
 * @param {string} type - Type for logging (start/end)
 * @returns {Timestamp|null} Firestore Timestamp or null if invalid
 */
const createTimestampFilter = (date, type) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    console.warn(`Invalid ${type} date:`, date);
    return null;
  }
  
  try {
    return Timestamp.fromDate(date);
  } catch (error) {
    console.error(`Error converting ${type} date to timestamp:`, error);
    return null;
  }
};

/**
 * Helper function to normalize date to start of day
 * @param {Date} date - Date to normalize
 * @returns {Date} Date set to start of day (00:00:00.000)
 */
const startOfDay = (date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

/**
 * Helper function to normalize date to end of day
 * @param {Date} date - Date to normalize
 * @returns {Date} Date set to end of day (23:59:59.999)
 */
const endOfDay = (date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

/**
 * Check if a quotation number already exists
 * @param {string} quotationNumber - Quotation number to check
 * @returns {Promise<boolean>} True if exists, false otherwise
 */
const checkQuotationNumberExists = async (quotationNumber) => {
  try {
    const q = query(
      collection(db, QUOTATIONS_COLLECTION),
      where('quotationNumber', '==', quotationNumber),
      limit(1)
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking quotation number existence:', error);
    return false;
  }
};

/**
 * Get the highest existing quotation number for a given year
 * @param {number} year - Year to check
 * @returns {Promise<number>} Highest counter number found
 */
const getHighestQuotationNumber = async (year) => {
  try {
    // Query for quotations in the specified year
    const q = query(
      collection(db, QUOTATIONS_COLLECTION),
      where('quotationNumber', '>=', `BS-01${year}`),
      where('quotationNumber', '<=', `BS-12${year}`),
      orderBy('quotationNumber', 'desc'),
      limit(50) // Get more results to find the highest counter
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) return 0;
    
    let highestCounter = 0;
    
    snapshot.forEach((doc) => {
      const quotationNumber = doc.data().quotationNumber;
      
      // Extract counter from BS-MMYYYY-XXX format
      const parts = quotationNumber.split('-');
      if (parts.length === 3) {
        const counter = parseInt(parts[2], 10);
        if (!isNaN(counter) && counter > highestCounter) {
          highestCounter = counter;
        }
      }
    });
    
    return highestCounter;
  } catch (error) {
    console.error('Error getting highest quotation number:', error);
    return 0;
  }
};

/**
 * Generate next quotation number with duplicate prevention
 * @returns {Promise<string>} Quotation number in format BS-MMYYYY-XXX
 */
export const generateQuotationNumber = async () => {
  const maxRetries = 5;
  let attempt = 0;
  
  while (attempt < maxRetries) {
    try {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const dateStr = `${month}${year}`;
      
      // Use transaction to ensure atomic counter increment
      const quotationNumber = await runTransaction(db, async (transaction) => {
        const counterRef = doc(db, QUOTATION_COUNTERS_COLLECTION, year.toString());
        const counterDoc = await transaction.get(counterRef);
        
        let nextNumber = 1;
        
        if (counterDoc.exists()) {
          nextNumber = counterDoc.data().count + 1;
          transaction.update(counterRef, {
            count: nextNumber,
            updatedAt: serverTimestamp()
          });
        } else {
          // Create new counter for this year
          transaction.set(counterRef, {
            count: 1,
            year: year.toString(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            manuallySet: false
          });
          nextNumber = 1;
        }
        
        return `BS-${dateStr}-${String(nextNumber).padStart(3, '0')}`;
      });
      
      // Double-check for duplicates
      const exists = await checkQuotationNumberExists(quotationNumber);
      if (!exists) {
        console.log(`✅ Generated unique quotation number: ${quotationNumber}`);
        return quotationNumber;
      }
      
      console.warn(`⚠️ Duplicate quotation number detected: ${quotationNumber}, retrying...`);
      attempt++;
      
    } catch (error) {
      console.error(`Error generating quotation number (attempt ${attempt + 1}):`, error);
      attempt++;
      
      if (attempt >= maxRetries) {
        // Final fallback - use timestamp with year-based format
        const now = new Date();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const timestamp = now.getTime().toString().slice(-6);
        const fallbackNumber = `BS-${month}${year}-${timestamp}`;
        
        console.error(`🚨 Using fallback quotation number: ${fallbackNumber}`);
        return fallbackNumber;
      }
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 100 * attempt));
    }
  }
  
  throw new Error('Failed to generate unique quotation number after maximum retries');
};

/**
 * Create a new quotation
 * @param {Object} quotationData - Quotation data
 * @returns {Promise<Object>} Created quotation with ID
 */
export const createQuotation = async (quotationData) => {
  try {
    const quotationNumber = await generateQuotationNumber();
    
    const quotation = {
      ...quotationData,
      quotationNumber,
      status: 'draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      version: 1
    };
    
    const docRef = await addDoc(collection(db, QUOTATIONS_COLLECTION), quotation);
    
    return {
      id: docRef.id,
      ...quotation,
      quotationNumber
    };
  } catch (error) {
    console.error('Error creating quotation:', error);
    throw error;
  }
};

/**
 * Update an existing quotation
 * @param {string} quotationId - Quotation ID
 * @param {Object} updates - Updated fields
 * @returns {Promise<boolean>} Success status
 */
export const updateQuotation = async (quotationId, updates) => {
  try {
    const quotationRef = doc(db, QUOTATIONS_COLLECTION, quotationId);
    await updateDoc(quotationRef, {
      ...updates,
      updatedAt: serverTimestamp(),
      version: increment(1)
    });
    return true;
  } catch (error) {
    console.error('Error updating quotation:', error);
    return false;
  }
};

/**
 * Get all quotations with real-time updates
 * @param {Function} callback - Function to call when data updates
 * @param {Object} options - Query options (status, startDate, endDate, limit, orderBy)
 * @returns {Function} Unsubscribe function
 */
export const subscribeToQuotations = (callback, options = {}) => {
  try {
    let q = collection(db, QUOTATIONS_COLLECTION);
    
    // Add filters
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    if (options.quoteRequestId) {
      q = query(q, where('quoteRequestId', '==', options.quoteRequestId));
    }
    
    // Add date range filters with proper validation and boundaries
    if (options.startDate) {
      const normalizedStartDate = startOfDay(options.startDate);
      const startTimestamp = createTimestampFilter(normalizedStartDate, 'start');
      if (startTimestamp) {
        q = query(q, where('createdAt', '>=', startTimestamp));
      }
    }
    
    if (options.endDate) {
      const normalizedEndDate = endOfDay(options.endDate);
      const endTimestamp = createTimestampFilter(normalizedEndDate, 'end');
      if (endTimestamp) {
        q = query(q, where('createdAt', '<=', endTimestamp));
      }
    }
    
    // Debug logging for date filtering
    if (options.startDate || options.endDate) {
      console.log('Quotation date filtering applied:', {
        startDate: options.startDate,
        endDate: options.endDate,
        normalizedStart: options.startDate ? startOfDay(options.startDate) : null,
        normalizedEnd: options.endDate ? endOfDay(options.endDate) : null
      });
    }
    
    // Add ordering
    const orderField = options.orderBy || 'createdAt';
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy(orderField, orderDirection));
    
    // Add limit
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    return onSnapshot(q, (snapshot) => {
      const quotations = [];
      snapshot.forEach((doc) => {
        quotations.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        });
      });
      callback(quotations);
    }, (error) => {
      console.error('Error fetching quotations:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up quotations subscription:', error);
    callback([]);
    return () => {};
  }
};

/**
 * Get quotations once (without real-time updates)
 * @param {Object} options - Query options (status, startDate, endDate, limit, orderBy)
 * @returns {Promise<Array>} Array of quotations
 */
export const getQuotations = async (options = {}) => {
  try {
    let q = collection(db, QUOTATIONS_COLLECTION);
    
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    // Add date range filters with proper validation and boundaries
    if (options.startDate) {
      const normalizedStartDate = startOfDay(options.startDate);
      const startTimestamp = createTimestampFilter(normalizedStartDate, 'start');
      if (startTimestamp) {
        q = query(q, where('createdAt', '>=', startTimestamp));
      }
    }
    
    if (options.endDate) {
      const normalizedEndDate = endOfDay(options.endDate);
      const endTimestamp = createTimestampFilter(normalizedEndDate, 'end');
      if (endTimestamp) {
        q = query(q, where('createdAt', '<=', endTimestamp));
      }
    }
    
    const orderField = options.orderBy || 'createdAt';
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy(orderField, orderDirection));
    
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const snapshot = await getDocs(q);
    const quotations = [];
    
    snapshot.forEach((doc) => {
      quotations.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      });
    });
    
    return quotations;
  } catch (error) {
    console.error('Error getting quotations:', error);
    return [];
  }
};

/**
 * Get a single quotation by ID
 * @param {string} quotationId - Quotation ID
 * @returns {Promise<Object|null>} Quotation data or null
 */
export const getQuotationById = async (quotationId) => {
  try {
    const quotationRef = doc(db, QUOTATIONS_COLLECTION, quotationId);
    const quotationSnap = await getDoc(quotationRef);
    
    if (quotationSnap.exists()) {
      return {
        id: quotationSnap.id,
        ...quotationSnap.data(),
        createdAt: quotationSnap.data().createdAt?.toDate(),
        updatedAt: quotationSnap.data().updatedAt?.toDate()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting quotation:', error);
    return null;
  }
};

/**
 * Delete a quotation
 * @param {string} quotationId - Quotation ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteQuotation = async (quotationId) => {
  try {
    await deleteDoc(doc(db, QUOTATIONS_COLLECTION, quotationId));
    return true;
  } catch (error) {
    console.error('Error deleting quotation:', error);
    return false;
  }
};

/**
 * Update quotation status
 * @param {string} quotationId - Quotation ID
 * @param {string} newStatus - New status
 * @returns {Promise<boolean>} Success status
 */
export const updateQuotationStatus = async (quotationId, newStatus) => {
  return updateQuotation(quotationId, { status: newStatus });
};

/**
 * Get quotation statistics
 * @param {Object} options - Options for date range filtering
 * @returns {Promise<Object>} Statistics object
 */
export const getQuotationStats = async (options = {}) => {
  try {
    const quotations = await getQuotations(options);
    
    const stats = {
      total: quotations.length,
      draft: quotations.filter(q => q.status === 'draft').length,
      sent: quotations.filter(q => q.status === 'sent').length,
      accepted: quotations.filter(q => q.status === 'accepted').length,
      rejected: quotations.filter(q => q.status === 'rejected').length,
      thisMonth: 0,
      thisWeek: 0
    };

    // Calculate this month and week stats only if no date range is specified
    if (!options.startDate && !options.endDate) {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
      
      stats.thisMonth = quotations.filter(q => 
        q.createdAt && q.createdAt >= startOfMonth
      ).length;
      
      stats.thisWeek = quotations.filter(q => 
        q.createdAt && q.createdAt >= startOfWeek
      ).length;
    }

    return stats;
  } catch (error) {
    console.error('Error getting quotation statistics:', error);
    return {
      total: 0,
      draft: 0,
      sent: 0,
      accepted: 0,
      rejected: 0,
      thisMonth: 0,
      thisWeek: 0
    };
  }
};

/**
 * Search quotations
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Filtered quotations
 */
export const searchQuotations = async (searchTerm) => {
  try {
    const quotations = await getQuotations();
    const searchLower = searchTerm.toLowerCase();
    
    return quotations.filter(quotation => 
      quotation.companyName?.toLowerCase().includes(searchLower) ||
      quotation.attn?.toLowerCase().includes(searchLower) ||
      quotation.email?.toLowerCase().includes(searchLower) ||
      quotation.quotationNumber?.toLowerCase().includes(searchLower)
    );
  } catch (error) {
    console.error('Error searching quotations:', error);
    return [];
  }
};

// Export status constants
export const QUOTATION_STATUSES = {
  DRAFT: 'draft',
  SENT: 'sent',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected'
};

export const QUOTATION_STATUS_LABELS = {
  [QUOTATION_STATUSES.DRAFT]: 'Draft',
  [QUOTATION_STATUSES.SENT]: 'Sent',
  [QUOTATION_STATUSES.ACCEPTED]: 'Accepted',
  [QUOTATION_STATUSES.REJECTED]: 'Rejected'
};

export const QUOTATION_STATUS_COLORS = {
  [QUOTATION_STATUSES.DRAFT]: '#8c8c8c',     // Gray
  [QUOTATION_STATUSES.SENT]: '#1890ff',     // Blue
  [QUOTATION_STATUSES.ACCEPTED]: '#52c41a', // Green
  [QUOTATION_STATUSES.REJECTED]: '#ff4d4f'  // Red
};

// Export quotation types
export const QUOTATION_TYPES = {
  PURCHASE: 'purchase',
  RENTAL: 'rental',
  RENTAL_EXTENSION: 'rental_extension',
  SCAFFOLDING_SERVICE: 'scaffolding_service'
};

export const QUOTATION_TYPE_LABELS = {
  [QUOTATION_TYPES.PURCHASE]: 'Purchase',
  [QUOTATION_TYPES.RENTAL]: 'Rental',
  [QUOTATION_TYPES.RENTAL_EXTENSION]: 'Rental Extension',
  [QUOTATION_TYPES.SCAFFOLDING_SERVICE]: 'Scaffolding Service'
};

// Export payment terms
export const PAYMENT_TERMS = {
  DEPOSIT_50: '50% deposit, rest upon installation of scaffold',
  FULL_100: '100% payment',
  CUSTOM: 'custom'
};

/**
 * Counter Management Functions for Emergency Use
 */

/**
 * Get current quotation counter value for a year
 * @param {number} year - Year to get counter for
 * @returns {Promise<Object>} Counter information
 */
export const getQuotationCounterValue = async (year) => {
  try {
    const counterRef = doc(db, QUOTATION_COUNTERS_COLLECTION, year.toString());
    const counterDoc = await getDoc(counterRef);
    
    if (counterDoc.exists()) {
      return {
        count: counterDoc.data().count,
        year: counterDoc.data().year,
        createdAt: counterDoc.data().createdAt?.toDate(),
        updatedAt: counterDoc.data().updatedAt?.toDate(),
        manuallySet: counterDoc.data().manuallySet || false
      };
    }
    
    return {
      count: 0,
      year: year.toString(),
      createdAt: null,
      updatedAt: null,
      manuallySet: false
    };
  } catch (error) {
    console.error('Error getting quotation counter value:', error);
    return null;
  }
};

/**
 * Set quotation counter value manually (for emergency use)
 * @param {number} year - Year to set counter for
 * @param {number} newValue - New counter value
 * @param {string} reason - Reason for manual change
 * @returns {Promise<Object>} Result with success status and message
 */
export const setQuotationCounterValue = async (year, newValue, reason = 'Manual adjustment') => {
  try {
    // Validate input
    if (!Number.isInteger(newValue) || newValue < 0) {
      return {
        success: false,
        message: 'Counter value must be a non-negative integer'
      };
    }
    
    // Get highest existing quotation number for this year to prevent duplicates
    const highestExisting = await getHighestQuotationNumber(year);
    
    if (newValue <= highestExisting) {
      return {
        success: false,
        message: `Cannot set counter to ${newValue}. Highest existing quotation number is ${highestExisting}. Set to ${highestExisting + 1} or higher.`,
        suggestedValue: highestExisting + 1
      };
    }
    
    // Set the counter value
    const counterRef = doc(db, QUOTATION_COUNTERS_COLLECTION, year.toString());
    await setDoc(counterRef, {
      count: newValue,
      year: year.toString(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      manuallySet: true,
      manualSetReason: reason,
      manualSetTimestamp: serverTimestamp()
    }, { merge: true });
    
    console.log(`✅ Quotation counter for ${year} manually set to ${newValue}. Reason: ${reason}`);
    
    return {
      success: true,
      message: `Counter successfully set to ${newValue}`,
      newValue: newValue
    };
    
  } catch (error) {
    console.error('Error setting quotation counter value:', error);
    return {
      success: false,
      message: `Error setting counter: ${error.message}`
    };
  }
};

/**
 * Get quotation counter information and validation
 * @param {number} year - Year to check
 * @returns {Promise<Object>} Detailed counter information
 */
export const getQuotationCounterInfo = async (year) => {
  try {
    const counterInfo = await getQuotationCounterValue(year);
    const highestExisting = await getHighestQuotationNumber(year);
    
    // Check for potential issues
    const issues = [];
    if (counterInfo.count <= highestExisting) {
      issues.push(`Counter (${counterInfo.count}) is not higher than highest existing quotation (${highestExisting})`);
    }
    
    return {
      counter: counterInfo,
      highestExisting: highestExisting,
      nextNumber: Math.max(counterInfo.count + 1, highestExisting + 1),
      issues: issues,
      isHealthy: issues.length === 0
    };
  } catch (error) {
    console.error('Error getting quotation counter info:', error);
    return null;
  }
};

/**
 * Reset quotation counter to safe value (highest existing + 1)
 * @param {number} year - Year to reset counter for
 * @returns {Promise<Object>} Result with success status and message
 */
export const resetQuotationCounterToSafe = async (year) => {
  try {
    const highestExisting = await getHighestQuotationNumber(year);
    const safeValue = highestExisting + 1;
    
    const result = await setQuotationCounterValue(year, safeValue, 'Auto-reset to safe value');
    
    if (result.success) {
      return {
        success: true,
        message: `Counter reset to safe value: ${safeValue}`,
        newValue: safeValue,
        previousHighest: highestExisting
      };
    }
    
    return result;
  } catch (error) {
    console.error('Error resetting quotation counter:', error);
    return {
      success: false,
      message: `Error resetting counter: ${error.message}`
    };
  }
};
